import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../utils/api";

const CurrencyContext = createContext();

const DEFAULT_DISPLAY_CURRENCIES = ["EUR"];
const STORAGE_KEY = "tabrams:selectedCurrency";

export const useCurrency = () => {
  const context = useContext(CurrencyContext);

  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }

  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [supportedCurrencies, setSupportedCurrencies] = useState(DEFAULT_DISPLAY_CURRENCIES);
  const [selectedCurrency, setSelectedCurrencyState] = useState(localStorage.getItem(STORAGE_KEY) || "EUR");
  const [exchangeRates, setExchangeRates] = useState({ EUR: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCurrencySettings = async () => {
      try {
        const { data } = await api.get("/settings/checkout");
        const configuredCurrencies = Array.isArray(data.settings?.display_currencies) && data.settings.display_currencies.length > 0
          ? Array.from(new Set(["EUR", ...data.settings.display_currencies]))
          : DEFAULT_DISPLAY_CURRENCIES;

        setSupportedCurrencies(configuredCurrencies);

        const savedCurrency = localStorage.getItem(STORAGE_KEY);
        const nextCurrency = configuredCurrencies.includes(savedCurrency) ? savedCurrency : "EUR";
        setSelectedCurrencyState(nextCurrency);

        const nonBaseCurrencies = configuredCurrencies.filter((currency) => currency !== "EUR");

        if (nonBaseCurrencies.length === 0) {
          setExchangeRates({ EUR: 1 });
          return;
        }

        const ratesResponse = await fetch(`https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${nonBaseCurrencies.join(",")}`);
        const rateRows = await ratesResponse.json();

        const nextRates = { EUR: 1 };
        if (Array.isArray(rateRows)) {
          rateRows.forEach((row) => {
            if (row.quote && row.rate) {
              nextRates[row.quote] = Number(row.rate);
            }
          });
        }

        setExchangeRates(nextRates);
      } catch (error) {
        console.error("Error loading currency settings:", error);
        setSupportedCurrencies(DEFAULT_DISPLAY_CURRENCIES);
        setSelectedCurrencyState("EUR");
        setExchangeRates({ EUR: 1 });
      } finally {
        setLoading(false);
      }
    };

    loadCurrencySettings();
  }, []);

  const setSelectedCurrency = (currency) => {
    if (!supportedCurrencies.includes(currency)) {
      return;
    }

    localStorage.setItem(STORAGE_KEY, currency);
    setSelectedCurrencyState(currency);
  };

  return (
    <CurrencyContext.Provider
      value={{
        exchangeRates,
        loading,
        selectedCurrency,
        setSelectedCurrency,
        supportedCurrencies,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};